import { Box, Grid } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { drawerListItems } from '@pages/PortalAdmin/PortalAdmin.constants';
import { Layout } from '@layout/Layout';
import { ThemeProvider } from '@mui/material/styles';
import composeTheme from '@theme/theme';
import { WIDTH_CONSTANTS } from '@theme/widthConstants';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { AdminDrawer, AdminDrawerListItem } from '@components/drawer/AdminDrawer';
import React, { useMemo, useCallback, Suspense } from 'react';
import { FullscreenLoading } from '@components/Loading';

// Custom loading component for admin portal that fills the content area
const AdminContentLoading = () => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        width: '100vw',
      }}
    >
      <FullscreenLoading />
    </Box>
  );
};

// Memoized main content component to prevent unnecessary re-renders
const MainContent = React.memo<{ isWidePageContent: boolean }>(({ isWidePageContent }) => (
  <Box
    component='main'
    sx={{
      flexGrow: 1,
      padding: { xs: 1, sm: 2 },
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
    }}
  >
    <Suspense fallback={<AdminContentLoading />}>
      <Grid
        data-testid='parent-grid'
        container
        style={{
          marginTop: 3,
          maxWidth: isWidePageContent ? '90vw' : WIDTH_CONSTANTS.xl,
        }}
        spacing={4}
        sx={{
          width: '100%',
          flexDirection: 'column',
          paddingTop: 2,
          alignItems: 'flex-end',
        }}
      >
        <Outlet />
      </Grid>
    </Suspense>
  </Box>
));

MainContent.displayName = 'MainContent';

export const PortalAdminLayout = () => {
  const navigate = useNavigate();
  const { isMobileOrSmallScreen } = useGetScreenSize();

  const isWidePageContent =
    window.location.pathname.includes('brokerage-management') ||
    window.location.pathname.includes('advanced-portal-configuration') ||
    window.location.pathname.includes('forms-v2-admin') ||
    window.location.pathname.includes('submission-drafts') ||
    window.location.pathname.includes('bor-transfer-requests') ||
    window.location.pathname.includes('api-client-management');

  // this is a workaround to prevent the page from being stuck on /portal-admin with no sub page.
  // happens in deployed does not seem to locally.. so here is to hoping
  if (window.location.pathname === '/portal-admin') {
    navigate('brokerage-management', { replace: true });
  }

  const adminDrawerItems: AdminDrawerListItem[] = useMemo(
    () =>
      drawerListItems.map((item) => ({
        name: item.name,
        icon: item.icon,
        path: item.path,
        roles: item.roles,
      })),
    [],
  );

  const handleToggleDrawer = useCallback(
    (newName: string | null) => {
      const selected = drawerListItems.find((item) => item.name === newName);
      navigate(selected?.path ?? 'brokerage-management');
    },
    [navigate],
  );

  if (isMobileOrSmallScreen) {
    // Use the original Layout component for mobile
    return (
      <ThemeProvider theme={composeTheme('light')}>
        <Layout
          title='Portal Admin'
          sideDrawerProps={{
            items: drawerListItems as any[],
            toggleDrawer: handleToggleDrawer,
          }}
        >
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Grid
              data-testid='parent-grid'
              container
              style={{
                marginTop: 3,
                maxWidth: isWidePageContent ? '90vw' : WIDTH_CONSTANTS.xl,
              }}
              spacing={4}
              sx={{
                width: '100%',
                flexDirection: 'column',
                paddingTop: 2,
                alignItems: 'flex-end',
              }}
            >
              <Outlet />
            </Grid>
          </Box>
        </Layout>
      </ThemeProvider>
    );
  }

  // Desktop layout with custom admin drawer
  return (
    <ThemeProvider theme={composeTheme('light')}>
      <Layout title='Portal Admin' config={{ padding: 0 }}>
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
          {/* Admin Navigation */}
          <AdminDrawer items={adminDrawerItems} toggleDrawer={handleToggleDrawer} />

          {/* Main content area */}
          <MainContent isWidePageContent={isWidePageContent} />
        </Box>
      </Layout>
    </ThemeProvider>
  );
};
